import React, { useEffect, useMemo, useState } from 'react';
import { ArticleSummaryLoadError, fetchArticleSummaries } from '../services/api';
import { ArticleSummary } from '../types/article';
import { formatDateYmd } from '../utils/time';
import { toWebpPath } from '../utils/image';
import { useContent } from '../contexts/ContentContext';
import { articleTitle, articleLead } from '../utils/articlePresentation';
import ContentPageHeader from './PageHeader';
import { useRestoreListPosition } from './BackToListButton';

interface ArticleGalleryProps {
    onDatesChange?: (dates: string[]) => void;
}


const ArticleGallery: React.FC<ArticleGalleryProps> = ({ onDatesChange }) => {
    const { t } = useContent();
    const [articles, setArticles] = useState<ArticleSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    useRestoreListPosition(!isLoading && !error);

    useEffect(() => {
        let active = true;
        const loadArticles = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const summaries = await fetchArticleSummaries();
                if (active) setArticles(summaries);
            } catch (loadError) {
                console.error('Failed to load article index:', loadError);
                if (active) {
                    setArticles(loadError instanceof ArticleSummaryLoadError ? loadError.articles : []);
                    setError(t('articlePage.error'));
                }
            } finally {
                if (active) setIsLoading(false);
            }
        };

        loadArticles();
        return () => { active = false; };
    }, [t, attempt]);

    const urlParams = new URLSearchParams(window.location.search);
    const filterYear = urlParams.get('year');
    const filterMonth = urlParams.get('month');
    const filterTag = urlParams.get('tag');

    const filteredArticles = useMemo(() => {
        return articles.filter(article => {
            if (filterYear && article.year !== filterYear) {
                return false;
            }
            if (filterMonth && article.month !== filterMonth) {
                return false;
            }
            if (filterTag) {
                const tags = article.tags || [];
                if (!tags.includes(filterTag)) {
                    return false;
                }
            }
            return true;
        });
    }, [articles, filterMonth, filterTag, filterYear]);

    const groupedByDate = useMemo(() => {
        const groups = new Map<string, ArticleSummary[]>();
        filteredArticles.forEach(article => {
            const dateKey = formatDateYmd(article.publishedAt) || (t('articlePage.unknownDate'));
            if (!groups.has(dateKey)) {
                groups.set(dateKey, []);
            }
            groups.get(dateKey)!.push(article);
        });
        return groups;
    }, [filteredArticles, t]);

    const sortedDates = useMemo(() => {
        return Array.from(groupedByDate.keys()).sort((a, b) => b.localeCompare(a));
    }, [groupedByDate]);

    useEffect(() => {
        if (onDatesChange) {
            onDatesChange(sortedDates);
        }
    }, [onDatesChange, sortedDates]);

    const heading = <ContentPageHeader sidebar count={!isLoading && !error ? t('notes.count', { count: filteredArticles.length }) : undefined} description={t('workPage.notesLead')} />;

    const feedback = <>{isLoading && <p role="status">{t('common.loading')}</p>}{error && <div role="alert"><p>{error}</p><button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div>}</>;

    if (isLoading && articles.length === 0) {
        return <section>{heading}<p role="status">{t('common.loading')}</p></section>;
    }

    if (error && articles.length === 0) {
        return <section>{heading}{feedback}</section>;
    }

    if (articles.length === 0) {
        return <section>{heading}<p>{t('articlePage.empty')}</p></section>;
    }

    if (filteredArticles.length === 0) {
        return <section>{heading}{feedback}{!error && !isLoading && <p>{t('articlePage.filteredEmpty')}</p>}</section>;
    }

    const getDisplayTitle = (article: ArticleSummary) => {
        return articleTitle(article.title, t('articlePage.untitled'));
    };

    const renderCard = (article: ArticleSummary, indexInGroup: number, dateKey: string) => {
        const dateId = dateKey.replace(/\//g, '-');
        const anchorId = indexInGroup === 0 ? `date-sep-${dateId}` : undefined;
        const publishedAtParam = article.publishedAt ? encodeURIComponent(String(article.publishedAt)) : '';
        const detailUrl = `/?page=article&view=detail&year=${article.year}&month=${article.month}&id=${article.id}${publishedAtParam ? `&published_at=${publishedAtParam}` : ''}`;
        const publishedLabel = formatDateYmd(article.publishedAt) || '';
        const hasCover = Boolean(article.coverImage);

        return (
            <a
                key={`${article.id}-${article.year}-${article.month}`}
                href={detailUrl}
                id={anchorId}
                className="article-card group flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 scroll-mt-24"
            >
                {hasCover && <div className="article-card-cover bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200"><img src={toWebpPath(article.coverImage)} alt="" className="w-full h-full object-cover" loading="lazy" /></div>}
                <div className="article-card-body p-5">
                    <h3 className="article-title text-sm font-bold text-gray-900 dark:text-gray-100 ivory:text-coffee-800 mb-2">
                        {getDisplayTitle(article)}
                    </h3>
                    {articleLead(getDisplayTitle(article), article.excerpt) && (
                        <p className="article-excerpt text-xs text-gray-600 dark:text-gray-300 ivory:text-coffee-600 whitespace-pre-line">
                            {articleLead(getDisplayTitle(article), article.excerpt)}
                        </p>
                    )}
                    <time className="lab-label">{publishedLabel}</time>
                </div>
            </a>
        );
    };

    return (
        <section className="lab-page lab-content-page">{heading}{feedback}<div className="article-grid">
            {sortedDates.flatMap((dateKey) => {
                const group = groupedByDate.get(dateKey) || [];
                return group.map((article, index) => renderCard(article, index, dateKey));
            })}
        </div></section>
    );
};

export default ArticleGallery;
